import type { OutputOf } from './generated/contracts';

/**
 * The rules and formats a bidding screen needs, on any platform.
 *
 * **These were in `apps/web/src/screens/` and a second client is exactly why they moved.**
 * Every one of them is a decision about how a bid is presented, not about how a browser
 * renders it: no DOM, no React, no styling. The alternative was `apps/mobile` re-deriving
 * them — and the comment on `nextBidAction` below is a first-hand account of what
 * re-deriving this particular rule costs.
 *
 * (No React in this package: the mobile bundler resolves shared packages with hierarchical
 * lookup off, so an import here would have to resolve from `apps/mobile/node_modules` and
 * could not. The constraint happens to draw the line in the right place.)
 */

export type AuctionView = OutputOf<'auctions.auction.get'>['auction'];
export type Position = OutputOf<'auctions.bid.position'>['position'];

/**
 * What this bidder's next action actually is — **one rule, three screens**.
 *
 * It was derived independently in the max-bid sheet (to pick the stepper's floor), on the
 * timed screen's button, and on the live panel's, and the third one got it wrong: it
 * offered "Bid $16" to somebody already winning at $15 with a max of $39. Tapping it
 * opened a sheet that said "Raise max to $40", because the sheet knew what the button did
 * not. A bidder holding the lead cannot outbid themselves; the only move they have is to
 * raise the ceiling, and a button that says otherwise is asking them to do something the
 * engine would refuse.
 *
 * Returned together so the label and the number it names cannot drift apart again — and
 * living here so a fourth screen cannot start the cycle over.
 */
export function nextBidAction(
  auction: AuctionView,
  position: Position | undefined,
): { raising: boolean; floorCents: number; label: string } {
  const raising = position?.standing === 'winning' && position.maxCents !== undefined;
  const floorCents = raising && position?.maxCents ? position.maxCents + auction.incrementCents : auction.nextBidCents;
  return {
    raising,
    floorCents,
    label: raising ? `Raise max to ${formatCents(floorCents)}` : `Bid ${formatCents(auction.nextBidCents)}`,
  };
}

/**
 * The quick-pick ladder on the max-bid sheet.
 *
 * **Ladders off the next bid, not round numbers pulled from nowhere** — a buyer is
 * choosing how much headroom to give, not picking a price. The steps are the artboard's:
 * two, six, twelve and eighteen increments above the floor.
 */
export function bidPicks(floorCents: number, incrementCents: number): number[] {
  return [2, 6, 12, 18].map((steps) => floorCents + incrementCents * steps);
}

/**
 * Money, the way the artboards write it: `$640`, not `$640.00`.
 *
 * Cents appear only when there are cents. A column of prices that all end in `.00` is
 * two characters of noise on every line of the one thing the buyer is reading, and the
 * design drops them everywhere.
 */
export function formatCents(cents: number): string {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    ...(whole ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {}),
  }).format(cents / 100);
}

/**
 * Time left, at the precision that matters at that distance.
 *
 * Days and hours when the auction is days away; minutes and seconds in the last hour,
 * because that is when a second is a decision.
 */
/**
 * How long an auction has left, by the server's clock rather than the device's (GO-170).
 *
 * `skewMs` is `serverNowMs − Date.now()` at the last read, so a phone whose clock is a
 * minute out shows the same countdown as one whose clock is right. Stops at zero: a closed
 * auction is settled by the next read, never by a screen deciding it has ended.
 */
export function remainingMs(endTimeMs: number, skewMs: number, deviceNowMs: number = Date.now()): number {
  return Math.max(0, endTimeMs - (deviceNowMs + skewMs));
}

/** A timed auction screen's poll while the close is far off. */
export const AUCTION_POLL_MS = 15_000;

/**
 * How long a timed auction screen waits before its next read (GO-173).
 *
 * A timed auction has no realtime channel, so the poll is how a bidder sees a competing
 * bid. Fifteen seconds is close enough while the next bid is minutes away, but it is
 * longer than the soft-close window, and the reset exists so the outbid bidder has time to
 * respond (GO-173 AC6). So inside the window the screen polls at half of it — five seconds
 * for today's ten — and a poll just outside waits only until the window opens. Read from
 * the auction's own window, so a seller's longer reset (GO-171) slows it to match; judged
 * by the server's clock (GO-170).
 */
export function auctionPollMs(
  auction: { endTimeMs: number; softCloseWindowMs: number },
  skewMs: number,
  deviceNowMs: number = Date.now(),
): number {
  const untilWindow = remainingMs(auction.endTimeMs, skewMs, deviceNowMs) - auction.softCloseWindowMs;
  return Math.max(auction.softCloseWindowMs / 2, Math.min(AUCTION_POLL_MS, untilWindow));
}

export function formatRemaining(ms: number): string {
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3_600);
  const minutes = Math.floor((total % 3_600) / 60);
  const seconds = total % 60;
  if (days > 0) return `${days}d ${String(hours).padStart(2, '0')}h`;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * How to name a seller on a buyer's screen.
 *
 * **`handle` is optional** — a seller admitted with nothing but a store name has none —
 * so the artboard's `@luxrelove` has to degrade rather than interpolate. It fell through
 * as a literal `@undefined` on the won screen, which is the shape of bug that only
 * appears against real data: every fixture has a handle.
 */
export function sellerLabel(seller: { handle?: string; displayName?: string } | undefined): string | undefined {
  if (!seller) return undefined;
  if (seller.handle) return `@${seller.handle}`;
  // The display name is not a handle, so it does not get an `@`.
  return seller.displayName || undefined;
}

/** "Thu, Sep 18 · 8:00 PM" — the artboard's line under the countdown. */
export function endsAtLabel(endTimeMs: number): string {
  const date = new Date(endTimeMs);
  const day = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${day} · ${time}`;
}

/**
 * "2h ago" — relative, because on this screen the distance is what matters, not the clock.
 *
 * `skewMs` is `serverNowMs − Date.now()` at the last read: every time on a bidding screen
 * is drawn through the server's clock, so a device with a wrong one shows the right
 * answer rather than a confidently wrong one.
 */
export function timeAgo(iso: string, skewMs: number): string {
  const seconds = Math.max(0, Math.floor((Date.now() + skewMs - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return 'just now';
  if (seconds < 3_600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3_600)}h ago`;
  return `${Math.floor(seconds / 86_400)}d ago`;
}

/**
 * The bid-state banner, as a decision rather than a rendering.
 *
 * The artboard's own note is the requirement: *"each carries a glyph as well as a color,
 * because a buyer has to read it without the label."* So the glyph is part of the state,
 * not a flourish a platform adds — and the copy is part of it too, because these sentences
 * explain proxy bidding at the only moment anybody reads about it.
 *
 * Extracted here for the reason written on `nextBidAction` above: this banner already
 * appears on the timed screen and the live panel, and a third copy on a phone is how the
 * three start disagreeing about what a bidder is being told.
 */
export type BidTone = 'leading' | 'outbid' | 'none';

export interface BidStatus {
  tone: BidTone;
  label: string;
  /** Read instead of the color, not alongside it. */
  glyph: '▲' | '▼' | '—';
  body: string;
}

export function bidStatus(auction: AuctionView, position: Position | undefined, signedIn: boolean): BidStatus {
  if (auction.status === 'closed_no_sale') {
    return {
      tone: 'none',
      label: 'Ended without a sale',
      glyph: '—',
      body:
        auction.liveBidCount === 0 ? 'Nobody bid on this one.' : 'The bidding did not reach the seller\u2019s minimum.',
    };
  }

  if (!signedIn || !position || position.standing === 'none') {
    return {
      tone: 'none',
      label: 'Not bidding',
      glyph: '—',
      // The one place proxy bidding is explained, on the screen where it is about to
      // happen. A buyer who thinks they are bidding the number on the button will feel
      // cheated by the engine doing exactly what it promised.
      body: `Every bid here is a max bid: you name the most you\u2019ll pay, and we bid the minimum needed to keep you in front — in ${formatCents(auction.incrementCents)} steps.`,
    };
  }

  if (position.standing === 'winning' || position.standing === 'won') {
    return {
      tone: 'leading',
      label: 'You\u2019re winning',
      glyph: '▲',
      body: `Your max of ${formatCents(position.maxCents ?? 0)} is holding. We bid in ${formatCents(auction.incrementCents)} steps and tell you the moment it breaks.`,
    };
  }

  // Behind at a price equal to your own max means a tie: an earlier max of the same amount
  // keeps the lead (GO-174). Anything that beats a max leaves the price above it.
  const max = position.maxCents ?? 0;
  const lostTie = auction.currentPriceCents === max;
  return {
    tone: 'outbid',
    label: 'Outbid',
    glyph: '▼',
    body: `${lostTie ? `Someone set a ${formatCents(max)} max before you.` : `Someone went past your ${formatCents(max)} max.`} ${formatCents(position.nextBidCents)} takes it back.`,
  };
}

/**
 * "Reserve not met", for a running auction whose seller's minimum is not yet met (GO-176).
 *
 * Never the amount: the server never sends it. Nothing once the reserve is met, as the
 * Listing Auctions PRD has it; the view still says `met`, so showing that is a client
 * change. Nothing on an auction with no reserve, and nothing once it is over, where the
 * outcome already says whether it sold.
 */
export function reserveLabel(auction: Pick<AuctionView, 'status' | 'reserve'>): string | undefined {
  if (auction.status !== 'active' || auction.reserve !== 'not_met') return undefined;
  return 'Reserve not met';
}

/** How long a screen shows the removal notice: long enough to read, gone before the next one. */
export const REMOVAL_NOTICE_MS = 15_000;

/**
 * "A bid was removed. The price is now $60." — for whoever was watching when a disqualified
 * bidder's bids came off this auction (GO-176).
 *
 * `seen` is the auction as this screen last had it. Nothing on the first load: someone who
 * opens the auction afterwards was never shown the price that changed. Never whose bid it
 * was: the server does not say, and the notice must not guess.
 */
export function removalNotice(
  seen: Pick<AuctionView, 'auctionId' | 'bidsRemovedAt'> | undefined,
  now: Pick<AuctionView, 'auctionId' | 'bidsRemovedAt' | 'currentPriceCents'>,
): string | undefined {
  if (seen === undefined || seen.auctionId !== now.auctionId) return undefined;
  if (now.bidsRemovedAt === undefined || now.bidsRemovedAt === seen.bidsRemovedAt) return undefined;
  return `A bid was removed. The price is now ${formatCents(now.currentPriceCents)}.`;
}

/**
 * The notice a screen shows after a new read: a fresh removal's, the one already showing if
 * it is still the same product, and nothing once the product changes or nothing is up. On
 * the live panel the next product can go up while a notice is showing (review on #124).
 */
export function nextRemovalNotice(
  current: string | undefined,
  seen: Pick<AuctionView, 'auctionId' | 'bidsRemovedAt'> | undefined,
  next: Pick<AuctionView, 'auctionId' | 'bidsRemovedAt' | 'currentPriceCents'> | undefined,
): string | undefined {
  if (next === undefined) return undefined;
  return removalNotice(seen, next) ?? (seen?.auctionId === next.auctionId ? current : undefined);
}

/** About a token and a half at the `hot` class's refill of three a second. */
export const BID_RETRY_DELAY_MS = 500;
/** A retry that cannot land this long before the close is not worth the wait. */
const BID_RETRY_MARGIN_MS = 1_000;

export interface BidRetryOptions {
  /** When the auction closes, as the server sent it. Absent: no deadline to respect. */
  endTimeMs?: number;
  /**
   * `serverNowMs − Date.now()` at the screen's last read. Without it the deadline would be
   * judged by the device's clock, which on a phone a minute fast skips the retry for the
   * last minute of every auction, and on one a minute slow retries into the close.
   */
  skewMs?: number;
  /** The device's clock; the server's is this plus `skewMs`. */
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  random?: () => number;
}

/**
 * **Place a bid, and ride out one rate limit without telling anybody.**
 *
 * `auctions.bid.place` is the `hot` rate class: ten in a burst, refilled at three a second,
 * per bidder. A bidder who hits it is almost always double-tapping in a bidding war, which is
 * exactly when an error costs them the item, and a token is back within about a third of a
 * second. The bid is naturally idempotent (the same max is the same bid), so sending it again
 * cannot place two.
 *
 * So one retry, after about half a second with jitter, while the screen still says it is
 * placing the bid. The error is shown instead when the retry could not land at least a second
 * before the close, and when the retry is refused too, because then it is not a burst. Any
 * other error is thrown as it came.
 */
export async function placeBidWithRetry<T>(place: () => Promise<T>, options: BidRetryOptions = {}): Promise<T> {
  const deviceNow = options.now ?? Date.now;
  const serverNow = () => deviceNow() + (options.skewMs ?? 0);
  const sleep = options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const random = options.random ?? Math.random;
  try {
    return await place();
  } catch (error) {
    if ((error as { code?: unknown } | null)?.code !== 'RATE_LIMITED') throw error;
    const delay = BID_RETRY_DELAY_MS + Math.round((random() - 0.5) * 200);
    if (options.endTimeMs !== undefined && serverNow() + delay + BID_RETRY_MARGIN_MS > options.endTimeMs) throw error;
    await sleep(delay);
    return place();
  }
}
